/**
 * Movement-replay hook — fetches the vehicle's oldest-first geolocated
 * touch-points from `GET /vehicles/{registration}/fraud-replay`. That endpoint
 * is a behavioural superset of `/replay`: same road-routed trail + points, but
 * every stop is additionally annotated with its TOP fraud reason
 * (`rule_id` / `severity` / `why_summary` / `is_fraud`) so the on-map blip popup
 * can render the forensic "WHY this stop is fraudulent" line. When `start`/`end`
 * are omitted the backend defaults them to the vehicle's full history and echoes
 * the resolved bounds on `period_start`/`period_end` so the panel can seed its
 * From/To pickers.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ReplayResponse } from '@/types/vehicles';

export function useVehicleReplay(
  registration: string | undefined,
  opts?: { start?: string; end?: string; limit?: number },
): UseQueryResult<ReplayResponse, Error> {
  return useQuery<ReplayResponse, Error>({
    queryKey: ['vehicle-fraud-replay', registration, opts?.start, opts?.end, opts?.limit],
    enabled: Boolean(registration),
    queryFn: async () => {
      const { data } = await api.get<ReplayResponse>(
        `/vehicles/${encodeURIComponent(registration as string)}/fraud-replay`,
        { params: { start: opts?.start, end: opts?.end, limit: opts?.limit ?? 2000 } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}
